/**
 * Partner-flow validation constants — single source of truth (FRONTEND).
 *
 * SYNC WARNING: These values are mirrored in the backend at
 *   crypto-asset-tracker-backend/src/common/constants/partner-validation.constant.ts
 * The two repos cannot share an import directly, so when you change a rule here you
 * MUST change it there too (ideally extract both into a shared workspace package).
 * Keeping them identical is what prevents frontend/backend validation drift.
 */

export const PARTNER_VALIDATION = {
  NAME: { MIN: 2, MAX: 100, REGEX: /^[a-zA-Z\s.'-]+$/ },
  COMPANY_NAME: { MIN: 2, MAX: 100, REGEX: /^[a-zA-Z0-9\s&.,'()-]+$/ },
  EMAIL: { MAX: 254, REGEX: /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/ },
  PHONE: { REGEX: /^\+?[0-9\s\-()]{7,18}$/ },
  ADDRESS: { MIN: 5, MAX: 200 },
  COMPANY_IDENTITY_NUMBER: { MIN: 5, MAX: 30, REGEX: /^[a-zA-Z0-9]+$/ },
  MESSAGE: { MAX: 1000 },
  // REGEX checks complexity only (upper/lower/special) and intentionally does NOT
  // constrain length — always validate via isValidPartnerPassword() which also
  // enforces MIN/MAX. bcrypt only hashes the first 72 bytes, hence MAX 72.
  PASSWORD: { MIN: 8, MAX: 72, REGEX: /^(?=.*[a-z])(?=.*[A-Z])(?=.*[^A-Za-z0-9]).+$/, SPECIAL_REGEX: /[^A-Za-z0-9]/ },
  OTP: { LENGTH: 6, REGEX: /^\d{6}$/ },
} as const;

/** Countries offered in the partner registration dropdown. Mirrored on the backend. */
export const PARTNER_COUNTRIES: string[] = [
  'United States', 'United Kingdom', 'Canada', 'Australia', 'Germany',
  'France', 'India', 'Singapore', 'United Arab Emirates', 'Japan',
  'Switzerland', 'Netherlands', 'Brazil', 'South Africa',
];

/** User-facing validation messages. Mirrored on the backend. */
export const PARTNER_VALIDATION_MESSAGES = {
  NAME: "Name must be 2-100 characters using only letters, spaces, and . ' -",
  COMPANY_NAME: "Company name must be 2-100 characters using only letters, numbers, spaces, and & . , ' ( ) -",
  EMAIL: 'Enter a valid email address',
  PHONE: 'Enter a valid phone number (7-18 digits, may start with +)',
  ADDRESS: 'Address must be 5-200 characters',
  COMPANY_IDENTITY_NUMBER: 'Company identity number must be 5-30 alphanumeric characters',
  MESSAGE: 'Message must not exceed 1000 characters',
  COUNTRY: 'Please select a valid country',
  PASSWORD:
    'Password must be at least 8 characters and include one uppercase letter, one lowercase letter, and one special character',
  OTP: 'Enter the 6-digit verification code',
};

/**
 * Full partner password rule used by every flow (settings change, get-started
 * signup, OTP activation, forgot-password reset). Enforces BOTH length
 * (MIN..MAX) and complexity (REGEX). Use this instead of `PASSWORD.REGEX.test()`
 * alone — the regex does not constrain length, so a bare test would accept short
 * passwords like "aA!". Mirrors the backend's @MinLength + @MaxLength + @Matches.
 */
export function isValidPartnerPassword(password: string): boolean {
  return (
    typeof password === 'string' &&
    password.length >= PARTNER_VALIDATION.PASSWORD.MIN &&
    password.length <= PARTNER_VALIDATION.PASSWORD.MAX &&
    PARTNER_VALIDATION.PASSWORD.REGEX.test(password)
  );
}
